import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { createPortal } from 'react-dom'
import debounce from 'lodash.debounce'
import useFloatGrid, { getFloatGridCustomProp } from '../../features/useFloatGrid'
import { Toggle } from '../../Objects/Toggle/Toggle'

/**
 * Wrapper component to group action/incognito buttons.
 * The ActionButtonsContainer is a container component without specific styling that places action/incognito buttons in the FloatGrid area.
 * Place the container anywhere in your document and the contained buttons will be automatically moved to the FloatGrid.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-action-buttons-container.html
 *
 * @param {React.ReactNode|React.ReactNodeArray} [children] - A collection of {@link Button}.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {string} [labelCollapsed] - The label when the component is collapsed.
 * @param {string} [labelExpanded] - The label when the component is expanded.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The ActionButtonsContainer component.
 * @constructor
 */
export function ActionButtonsContainer({ children, className, labelCollapsed, labelExpanded, testId, otherProps }) {
  const [area, setArea] = useState(null)
  const [scrollCollapsed, setScrollCollapsed] = useState(false)
  const floatGrid = useFloatGrid(area)
  const ref = useRef(null)
  const itemsRef = useRef(null)

  useEffect(() => {
    if (!area) {
      setTimeout(() => {
        // Get custom prop from CSS.
        setArea(getFloatGridCustomProp(ref?.current))
      }, 0)
    }
  }, [ref, area])

  useEffect(() => {
    const handleScroll = debounce(() => requestAnimationFrame(() => setScrollCollapsed(window.scrollY > 250)), 250)
    document.addEventListener('scroll', handleScroll)
    return () => document.removeEventListener('scroll', handleScroll)
  })

  const classNameList = classNames('c-action-buttons-container', className).trim()
  const toggleClassNameList = classNames('c-action-buttons-container__toggle', 'js-action-buttons-container-toggle', {
    'is-collapsed': scrollCollapsed,
  }).trim()

  const comp = (
    <div className={classNameList} data-testid={testId} ref={ref} {...otherProps}>
      {/* {@link Toggle} object collapsible area. */}
      <div className="c-action-buttons-container__items" id="action-buttons-container-items" ref={itemsRef}>
        {React.Children.map(children, (child) =>
          React.cloneElement(child, { className: 'c-action-buttons-container__item' })
        )}
      </div>

      {/* Use {@link Toggle} object here. */}
      <Toggle
        className={toggleClassNameList}
        defaultHidden
        id="action-buttons-container-items-toggle"
        labelHidden={labelCollapsed}
        labelUnhidden={labelExpanded}
        targetId="action-buttons-container-items"
        targetRef={itemsRef}
        /**
         * Pass in {@link Button.propTypes}.
         */
        action
        icon
        iconClassName="c-action-buttons-container__toggle__icon"
        iconViaCss
        incognito
      />
    </div>
  )
  return area ? createPortal(comp, floatGrid) : comp
}

ActionButtonsContainer.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.array]), // Bronson template: 'buttons'.
  className: PropTypes.string, // Additional CSS classes on the component element.
  labelCollapsed: PropTypes.string,
  labelExpanded: PropTypes.string,
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}
